"use client";

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Modal from "react-modal";
import {
  FaEye,
  FaArrowLeft,
  FaArrowRight,
  FaSearch,
  FaWhatsapp,
  FaGift,
  FaClipboardList,
} from "react-icons/fa";

// Reutiliza o estilo base do VendasDono (tema escuro + espaçamento do sidebar)
import "../styles/vendasDono.css";
// Estilos específicos/overrides desta tela
import "../styles/raspadinhaVendas.css";

Modal.setAppElement("#root");

const API = "https://api.ticketdovaqueiro.com/api";

function formatBRL(v) {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleString("pt-BR");
}
function maskWhatsapp(w) {
  if (!w) return "-";
  const s = String(w).replace(/\D/g, "");
  if (s.length >= 12) {
    return `+${s.slice(0, 2)} (${s.slice(2, 4)}) ${s.slice(4, 9)}-${s.slice(9, 13)}`;
  }
  return s;
}

const RaspadinhaVendasAdmin = () => {
  const [eventos, setEventos] = useState([]);
  const [eventoId, setEventoId] = useState("");
  const [categorias, setCategorias] = useState([]);
  const [categoria, setCategoria] = useState("");
  const [status, setStatus] = useState("pago"); // padrão = pago

  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [pageSize] = useState(10);

  const [loadingEventos, setLoadingEventos] = useState(false);
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [loadingVendas, setLoadingVendas] = useState(false);

  const [lista, setLista] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalValor, setTotalValor] = useState(0);
  const [totalQtd, setTotalQtd] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [pedidoIdModal, setPedidoIdModal] = useState(null);
  const [detalhePedido, setDetalhePedido] = useState(null);
  const [entries, setEntries] = useState([]);

  // Carrega eventos para o admin
  useEffect(() => {
    const run = async () => {
      try {
        setLoadingEventos(true);
        const token = localStorage.getItem("token");
        const r = await axios.get(`${API}/events/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEventos(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        console.error("Erro ao buscar eventos:", e?.message);
      } finally {
        setLoadingEventos(false);
      }
    };
    run();
  }, []);

  // Carrega categorias ao selecionar evento
  useEffect(() => {
    const run = async () => {
      if (!eventoId) {
        setCategorias([]);
        setCategoria("");
        return;
      }
      try {
        setLoadingCategorias(true);
        const token = localStorage.getItem("token");
        const r = await axios.get(`${API}/events/${eventoId}/categorias`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // pode vir um array de strings ou objetos { nome }
        const arr = Array.isArray(r.data) ? r.data : [];
        const nomes = arr.map((c) => (typeof c === "string" ? c : c?.nome)).filter(Boolean);
        setCategorias(nomes);
      } catch (e) {
        console.error("Erro ao buscar categorias:", e?.message);
      } finally {
        setLoadingCategorias(false);
      }
    };
    run();
  }, [eventoId]);

  // Busca vendas paginadas
  const carregarVendas = async (pg = 1) => {
    if (!eventoId || !categoria) {
      setLista([]);
      setTotalCount(0);
      setTotalPages(1);
      setTotalValor(0);
      setTotalQtd(0);
      setPagina(1);
      return;
    }
    try {
      setLoadingVendas(true);
      const token = localStorage.getItem("token");
      const url = `${API}/raspadinha/vendas?eventoId=${eventoId}&categoria=${encodeURIComponent(
        categoria
      )}&page=${pg}&pageSize=${pageSize}&status=${status || ""}`;
      const r = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = r.data || {};
      setLista(Array.isArray(data.items) ? data.items : []);
      setTotalCount(Number(data.totalCount || 0));
      setTotalPages(Number(data.totalPages || 1));
      setTotalValor(Number(data.totalValor || 0));
      setTotalQtd(Number(data.totalQtd || 0));
      setPagina(pg);
    } catch (e) {
      console.error("Erro ao buscar vendas raspadinha:", e?.message);
    } finally {
      setLoadingVendas(false);
    }
  };

  useEffect(() => {
    carregarVendas(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId, categoria, status, pageSize]);

  // Abrir modal de detalhes: carrega pedido + entries
  const abrirModal = async (pedidoId) => {
    try {
      setPedidoIdModal(pedidoId);
      setModalOpen(true);
      setDetalhePedido(null);
      setEntries([]);

      const [rPed, rEnt] = await Promise.all([
        axios.get(`${API}/pedidos/${pedidoId}`),
        axios.get(`${API}/raspadinha/entries-by-pedido/${pedidoId}`),
      ]);

      setDetalhePedido(rPed.data || null);
      setEntries(Array.isArray(rEnt.data) ? rEnt.data : []);
    } catch (e) {
      console.error("Erro ao abrir modal:", e?.message);
    }
  };
  const fecharModal = () => {
    setModalOpen(false);
    setPedidoIdModal(null);
    setDetalhePedido(null);
    setEntries([]);
  };

  // Busca local por nome/whats
  const filtrados = useMemo(() => {
    if (!busca) return lista;
    const q = busca.toLowerCase();
    return lista.filter((i) => {
      const nome = i?.usuario?.nome || "";
      const zap = i?.usuario?.whatsapp || "";
      return nome.toLowerCase().includes(q) || String(zap).toLowerCase().includes(q);
    });
  }, [lista, busca]);

  const podeVoltar = pagina > 1;
  const podeAvancar = pagina < totalPages;

  // Inferir ganho: se houver 3+ faces iguais
  const inferiuGanho = (e) => {
    const faces = Array.isArray(e?.faces) ? e.faces : [];
    const count = faces.reduce((acc, f) => {
      acc[f] = (acc[f] || 0) + 1;
      return acc;
    }, {});
    return Object.values(count).some((n) => n >= 3);
  };

  return (
    <div className="vendas-dono">
      <div className="rv-vendas-content">
        <h2>
          <FaGift style={{ marginRight: 10 }} />
          Vendas da Raspadinha
        </h2>

        {/* Filtros (mesma estrutura visual do VendasDono) */}
        <div className="filtros">
          <select
            value={eventoId}
            onChange={(e) => setEventoId(e.target.value)}
            disabled={loadingEventos}
          >
            <option value="">Selecione um evento</option>
            {eventos.map((ev) => (
              <option key={ev._id} value={ev._id}>
                {ev.nome}
              </option>
            ))}
          </select>

          <select
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            disabled={!eventoId || loadingCategorias}
          >
            <option value="">Selecione uma categoria</option>
            {categorias.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Todos os status</option>
            <option value="pago">Pago</option>
            <option value="pendente">Pendente</option>
            <option value="cancelado">Cancelado</option>
          </select>

          <div className="search-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por nome ou WhatsApp"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              disabled={!eventoId || !categoria}
            />
          </div>

          <button onClick={() => carregarVendas(1)}>
            Buscar
          </button>
        </div>

        {/* KPI cards (estilo compatível) */}
        <div className="rv-kpis">
          <div className="rv-kpi">
            <span>Total de raspadinhas</span>
            <strong>{totalQtd}</strong>
          </div>
          <div className="rv-kpi">
            <span>Total vendido</span>
            <strong>{formatBRL(totalValor)}</strong>
          </div>
          <div className="rv-kpi">
            <span>Pedidos listados</span>
            <strong>{totalCount}</strong>
          </div>
        </div>

        {/* Tabela (usa o mesmo container e tabela do VendasDono) */}
        <div className="table-container">
          <table className="tabela-vendas">
            <thead>
              <tr>
                <th>Data</th>
                <th>Cliente</th>
                <th>WhatsApp</th>
                <th>Categoria</th>
                <th>Qtde</th>
                <th>Valor</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loadingVendas ? (
                <tr>
                  <td colSpan="8" className="loading-cell">Carregando…</td>
                </tr>
              ) : filtrados.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-cell">Nenhum resultado.</td>
                </tr>
              ) : (
                filtrados.map((row) => (
                  <tr key={row.pedidoId}>
                    <td>{formatDate(row.dataCriacao)}</td>
                    <td>{row.usuario?.nome || "-"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <FaWhatsapp style={{ marginRight: 6, color: "#25D366" }} />
                      {maskWhatsapp(row.usuario?.whatsapp)}
                    </td>
                    <td>{row.categoria || "-"}</td>
                    <td>{row.qtdComprada}</td>
                    <td>{formatBRL(row.valorTotal)}</td>
                    <td>
                      <span
                        className={
                          row.statusPagamento === "pago"
                            ? "status-pago"
                            : row.statusPagamento === "pendente"
                            ? "status-pendente"
                            : "status-cancelado"
                        }
                      >
                        {row.statusPagamento || "-"}
                      </span>
                    </td>
                    <td>
                      <FaEye
                        className="icon-action"
                        title="Ver detalhes"
                        onClick={() => abrirModal(row.pedidoId)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        <div className="rv-paginacao">
          <button disabled={! (pagina > 1)} onClick={() => carregarVendas(pagina - 1)}>
            <FaArrowLeft style={{ marginRight: 8 }} />
            Anterior
          </button>
          <span>
            Página <strong>{pagina}</strong> de <strong>{totalPages}</strong>
          </span>
          <button disabled={! (pagina < totalPages)} onClick={() => carregarVendas(pagina + 1)}>
            Próxima
            <FaArrowRight style={{ marginLeft: 8 }} />
          </button>
        </div>

        {/* Modal de detalhes — mantém visual do VendasDono */}
        <Modal
          isOpen={modalOpen}
          onRequestClose={fecharModal}
          className="hg-modal-content"
          overlayClassName="hg-modal-overlay"
        >
          <button className="hg-close-button" onClick={fecharModal}>×</button>

          <div className="hg-modal-header">
            <FaClipboardList className="hg-modal-icon" />
            <h2>Detalhes do Pedido</h2>
          </div>

          {!detalhePedido ? (
            <div className="loading-cell">Carregando…</div>
          ) : (
            <div className="hg-modal-body">
              <div className="hg-detail-section">
                <h3>Informações do Pedido</h3>
                <div className="hg-detail-grid">
                  <div className="hg-detail-item">
                    <span className="hg-detail-label">Evento:</span>
                    <span className="hg-detail-value">{detalhePedido?.evento?.nome || "-"}</span>
                  </div>
                  <div className="hg-detail-item">
                    <span className="hg-detail-label">Categoria:</span>
                    <span className="hg-detail-value">{detalhePedido?.categoria || "-"}</span>
                  </div>
                  <div className="hg-detail-item">
                    <span className="hg-detail-label">Status Pagamento:</span>
                    <span className="hg-detail-value">{detalhePedido?.statusPagamento || "-"}</span>
                  </div>
                  <div className="hg-detail-item">
                    <span className="hg-detail-label">Data do Pedido:</span>
                    <span className="hg-detail-value">{formatDate(detalhePedido?.dataCriacao)}</span>
                  </div>
                  <div className="hg-detail-item">
                    <span className="hg-detail-label">Qtde Raspadinhas:</span>
                    <span className="hg-detail-value">{detalhePedido?.raspadinha?.qtdComprada || 0}</span>
                  </div>
                  <div className="hg-detail-item">
                    <span className="hg-detail-label">Total Raspadinha:</span>
                    <span className="hg-detail-value">{formatBRL(detalhePedido?.raspadinha?.valorTotal || 0)}</span>
                  </div>
                </div>
              </div>

              <div className="hg-detail-section">
                <h3>Cliente</h3>
                <div className="hg-detail-grid">
                  <div className="hg-detail-item">
                    <span className="hg-detail-label">Nome:</span>
                    <span className="hg-detail-value">{detalhePedido?.usuario?.nomeCompleto || "-"}</span>
                  </div>
                  <div className="hg-detail-item">
                    <span className="hg-detail-label">E-mail:</span>
                    <span className="hg-detail-value">{detalhePedido?.usuario?.email || "-"}</span>
                  </div>
                  <div className="hg-detail-item">
                    <span className="hg-detail-label">WhatsApp:</span>
                    <span className="hg-detail-value">{maskWhatsapp(detalhePedido?.usuario?.whatsapp)}</span>
                  </div>
                </div>
              </div>

              <div className="hg-detail-section">
                <h3>Raspadinhas</h3>
                <div className="rv-entries">
                  {entries.length === 0 ? (
                    <div className="empty-cell">Sem entries (pode não estar liberado).</div>
                  ) : (
                    entries.map((e, idx) => {
                      const ganhou = inferiuGanho(e);
                      return (
                        <div key={e._id} className={`rv-entry ${ganhou ? "win" : "loss"}`}>
                          <div className="rv-entry__top">
                            <span className="rv-entry__idx">#{idx + 1}</span>
                            <span className={`rv-entry__badge ${ganhou ? "ok" : "no"}`}>
                              {ganhou ? "GANHOU" : "NÃO FOI DESSA VEZ"}
                            </span>
                          </div>
                          <div className="rv-entry__body">
                            <div className="rv-entry__faces">
                              {(e.faces || []).slice(0, 6).map((f, i) => (
                                <span key={i} className="rv-face">{f}</span>
                              ))}
                            </div>
                            <div className="rv-entry__prize">
                              <span className="rv-entry__prize-label">Prêmio:</span>
                              <strong>{e?.nomePremio || "-"}</strong>
                            </div>
                            <div className="rv-entry__time">
                              Raspada: {e.scratched ? formatDate(e.createdAt) : "—"}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default RaspadinhaVendasAdmin;
